import * as React from "react";
import classNames from "classnames";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import { StoreState } from "../../reducers/root-reducer";
import { OccupiedDay } from "../../api/occupied-days";
import {
	WithStyles,
	Theme,
	createStyles,
	withStyles,
	Typography
} from "@material-ui/core";
import { translate, InjectedTranslateProps } from "react-i18next";

import {
	setDate,
	openAddReservationDialog,
	activateTableLinkMode,
	activateTableLinkDeletionMode
} from "../../actions/store/app-actions";

import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import LayersIcon from "@material-ui/icons/Layers";
import HelpIcon from "@material-ui/icons/Help";
import LockIcon from "@material-ui/icons/Lock";
import LinkIcon from "@material-ui/icons/Link";
import EventNoteIcon from "@material-ui/icons/EventNote";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import { formatDate } from "../../util/date-utility";
import {
	showAdditionalInfo,
	hideAdditionalInfo,
	toggleInfoBar,
	setActiveArea
} from "../../actions/store/overview-actions";
import { DatePicker, ConnectedDatePicker } from "../datepicker";
import {
	toggleLockForSelectedDay,
	toggleAreaLockForSelectedDay	
} from "../../actions/occupied-day-actions";
import { Area } from "../../api/areas";
import { ConnectedAreaSelect } from "./area-select";
import { ConnectedDateSelect, DateSelect } from "./date-select";
import { ConnectedMobileMenu } from "./mobile-menu";
import { ConnectedAddReservationButton } from "./add-reservation-button";
 
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import {
	getSelectedOccupiedDay,
	isAreaLocked
} from "../../util/general-utility";

export interface PlannerItemsProps {}

interface State {
	showLockAreaDialog: boolean;
	linkTablesMenuAnchor: HTMLElement;
}

interface ConnectedPlannerItemsProps extends WithStyles<typeof styles> {
	occupiedDays: OccupiedDay[];
	selectedDate: Date;
	additionalInfoVisible: boolean;
	activeAreaId: string;
	layout: "desktop" | "tablet" | "phone";
	setDate: (date: Date) => void;
	showAdditionalInfo: () => void;
	hideAdditionalInfo: () => void;
	activateTableLinkMode: () => void;
	activateTableLinkDeletionMode: () => void;
	toggleInfoBar: () => void;
}

type Props = PlannerItemsProps &
	ConnectedPlannerItemsProps &
	InjectedTranslateProps;

export class PlannerItems extends React.Component<Props, State> {
	private helpButtonRef: HTMLButtonElement;
	private helpButtonTimer: number = null;

	public state: State = {
		linkTablesMenuAnchor: null,
		showLockAreaDialog: false
	};

	componentWillUnmount() {
		if (this.helpButtonRef) {
			this.helpButtonRef.removeEventListener(
				"mousedown",
				this.handleHelpButtonPress
			);
			this.helpButtonRef.removeEventListener(
				"touchstart",
				this.handleHelpButtonPress
			);
			this.helpButtonRef.removeEventListener(
				"mouseup",
				this.handleHelpButtonPress
			);
			this.helpButtonRef.removeEventListener(
				"touchend",
				this.handleHelpButtonPress
			);
		}
	}

	handleShowDetailsButtonClick = () => {
		this.props.toggleInfoBar();
	};

	handleHelpButtonPress = (event: MouseEvent | TouchEvent) => {
		if (!event.defaultPrevented) {
			event.preventDefault();

			this.helpButtonTimer = setTimeout(() => {
				this.props.showAdditionalInfo();
			}, 300);
		}
	};

	handleHelpButtonRelease = (event: MouseEvent | TouchEvent) => {
		if (!event.defaultPrevented) {
			event.preventDefault();

			clearTimeout(this.helpButtonTimer);

			const {
				additionalInfoVisible,
				hideAdditionalInfo,
				toggleInfoBar
			} = this.props;

			if (additionalInfoVisible) {
				hideAdditionalInfo();
			} else {
				toggleInfoBar();
			}
		}
	};

	handleHelpButtonRef = (element: HTMLButtonElement) => {
		this.helpButtonRef = element;

		if (element) {
			element.addEventListener("mousedown", this.handleHelpButtonPress);
			element.addEventListener("touchstart", this.handleHelpButtonPress);
			element.addEventListener("mouseup", this.handleHelpButtonRelease);
			element.addEventListener("touchend", this.handleHelpButtonRelease);
		}
	};

	handleLockdownButtonClick = () => {
		const { activeAreaId } = this.props;

		if (this.isCurrentAreaLocked()) {
			toggleAreaLockForSelectedDay(activeAreaId, false);
		} else {
			this.handleLockAreaDialogToggle(true);
		}
	};

	isCurrentAreaLocked = () => {
		const { activeAreaId, selectedDate, occupiedDays } = this.props;

		if (occupiedDays.length === 0) return;

		const occupiedDay = getSelectedOccupiedDay(occupiedDays, selectedDate);
		const lockedAreas = (occupiedDay && occupiedDay.lockedAreas) || [];

		return (lockedAreas.indexOf(activeAreaId) > -1);
	};

	handleLockAreaDialogToggle = (openDialog: boolean) => {
		this.setState({
			showLockAreaDialog: openDialog
		});
	};

	handleLockAreaButtonClick = (sendAreaNotifications: boolean) => {
		const { activeAreaId } = this.props;

		this.handleLockAreaDialogToggle(false);
		toggleAreaLockForSelectedDay(activeAreaId, sendAreaNotifications);
	};

	handleLinkTablesButtonClick = (
		event: React.MouseEvent<HTMLButtonElement>
	) => {
		this.setState({ linkTablesMenuAnchor: event.currentTarget });
	};

	handleLinkTablesMenuClose = () => {
		this.setState({ linkTablesMenuAnchor: null });
	};

	handleTableLinkMenuItemClick = () => {
		this.props.activateTableLinkMode();
		this.handleLinkTablesMenuClose();
	};

	handleTableLinkDeletionMenuItemClick = () => {
		this.props.activateTableLinkDeletionMode();
		this.handleLinkTablesMenuClose();
	};

	renderLinkTablesMenuItem() {
		const { classes } = this.props;

		return (
			<MenuItem
				className={classes.activateLinkModeMenuItem}
				onClick={this.handleTableLinkMenuItemClick}
			>
				Tische verbinden
			</MenuItem>
		);
	}

	renderRemoveLinksMenuItem() {
		const { classes } = this.props;

		return (
			<MenuItem
				className={classes.activateLinkDeletionModeMenuItem}
				onClick={this.handleTableLinkDeletionMenuItemClick}
			>
				Verbindungen aufheben
			</MenuItem>
		);
	}

	renderLeftItems() {
		const { classes, selectedDate } = this.props;

		return (
			<React.Fragment> 
				<ConnectedAreaSelect />
			</React.Fragment>
		);
		/*
		return (
			<React.Fragment>
				<ConnectedDateSelect />
				<ConnectedAreaSelect />
			</React.Fragment>
		);
		*/
	}

	renderRightItems() {
		const { classes } = this.props;
		const { linkTablesMenuAnchor, showLockAreaDialog } = this.state;

		return (
			<React.Fragment>
				<ConnectedAddReservationButton />
				<IconButton
					color="inherit"
					className={classes.linkTablesButton}
					onClick={this.handleLinkTablesButtonClick}
				>
					<LinkIcon />
				</IconButton>
				<Menu
					anchorEl={linkTablesMenuAnchor}
					open={Boolean(linkTablesMenuAnchor)}
					onClick={this.handleLinkTablesMenuClose}
				>
					{this.renderLinkTablesMenuItem()}
					{this.renderRemoveLinksMenuItem()}
				</Menu>
				<IconButton
					color="inherit"
					className={classes.lockdownButton}
					onClick={this.handleLockdownButtonClick}
				>
					<LockIcon />
				</IconButton>
				<IconButton
					color="inherit"
					className={classes.infoButton}
					buttonRef={this.handleHelpButtonRef}
					onContextMenu={e => e.preventDefault()}
				>
					<HelpIcon />
				</IconButton>

				<Dialog
					open={showLockAreaDialog}  
				>
					<DialogTitle>E-Mail Benachrichtigung</DialogTitle> 
					<DialogContent>
						<DialogContentText>
							Soll eine Tisch-Bestätigungsemail an <strong>alle eingeteilten Reservierungen</strong> versendet werden?
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button
							className={classes.cancelButton}
							onClick={() => this.handleLockAreaDialogToggle(false)} >
							Abbrechen
						</Button>
						<Button onClick={() => this.handleLockAreaButtonClick(false)} >
							Nein
						</Button>
						<Button onClick={() => this.handleLockAreaButtonClick(true)} >
							Senden
						</Button>
					</DialogActions>
				</Dialog>	

			</React.Fragment>
		);
	}

	private renderPhone() {
		return (
			<React.Fragment>
				<ConnectedMobileMenu>
					{this.renderLinkTablesMenuItem()}
					{this.renderRemoveLinksMenuItem()}
					<MenuItem onClick={this.handleLockdownButtonClick}>
						Tag sperren/entsperren
					</MenuItem>
					<MenuItem onClick={this.handleShowDetailsButtonClick}>
						Details umschalten
					</MenuItem>
				</ConnectedMobileMenu>
			</React.Fragment>
		);
	}

	public render() {
		const { classes, layout } = this.props;

		if (layout === "phone") {
			return (
				<React.Fragment> 
					<ConnectedAreaSelect />
					<div className={classes.flexExpand} />
					<ConnectedAddReservationButton />
					{this.renderPhone()}
				</React.Fragment>
			);
			/*
			return (
				<React.Fragment>
					<ConnectedDateSelect />
					<ConnectedAreaSelect />
					<div className={classes.flexExpand} />
					<ConnectedAddReservationButton />
					{this.renderPhone()}
				</React.Fragment>
			);
			*/
		}

		return (
			<React.Fragment>
				{this.renderLeftItems()}
				<div className={classes.flexExpand} />
				{this.renderRightItems()}
			</React.Fragment>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	selectedDate: state.date,
	additionalInfoVisible: state.overviewInfoVisible,
	activeAreaId: state.overviewActiveAreaId,
	layout: state.layout,
	occupiedDays: state.occupiedDays
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	setDate: (date: Date) => dispatch(setDate(date)),
	showAdditionalInfo: () => dispatch(showAdditionalInfo()),
	hideAdditionalInfo: () => dispatch(hideAdditionalInfo()),
	activateTableLinkMode: () => dispatch(activateTableLinkMode()),
	activateTableLinkDeletionMode: () =>
		dispatch(activateTableLinkDeletionMode()),
	toggleInfoBar: () => dispatch(toggleInfoBar())
});

const styles = (theme: Theme) =>
	createStyles({
		iconSmall: {
			fontSize: 20
		},
		iconButtonLabel: {
			textTransform: "none",
			paddingLeft: 6,
			paddingRight: 6
		},
		infoButton: {},
		linkTablesButton: {},
		lockdownButton: {},
		activateLinkModeMenuItem: {},
		activateLinkDeletionModeMenuItem: {},
		dateSelectButton: {
			marginRight: 8
		},
		dropdownButton: {
			paddingLeft: 8,
			paddingRight: 8,
			borderColor: "rgba(0, 0, 0, 0.3)"
		},
		hidden: {
			position: "absolute",
			zIndex: -55,
			visibility: "hidden"
		},
		flexExpand: {
			flexGrow: 1
		},
		cancelButton: {
			marginRight: "auto"
		},
	});

export const ConnectedPlannerItems = translate("translations")(
	withStyles(styles)(
		connect(
			mapStateToProps,
			mapDispatchToProps
		)(PlannerItems)
	)
);
